import React, { useState } from "react";
import {
  Card,
  List,
  Tag,
  Button,
  Space,
  Badge,
  Empty,
  Tooltip,
  Switch,
} from "antd";
import {
  RiAlertLine,
  RiErrorWarningLine,
  RiInformationLine,
  RiMapPin5Fill,
  RiCheckLine,
  RiCloseLine,
  RiEyeLine,
  RiEyeOffLine,
  RiArrowLeftRightLine,
} from "react-icons/ri";
import moment from "moment";

// `line_cross:<tripwireId>` is the alert `type` for a 越線 (cross-line)
// event (spec "Live wiring" / metadata shape). `metadata.uncertain` /
// `metadata.degraded` are set by the backend crossing algorithm (spec "Pure
// algorithm") -- surfaced as small badges rather than folded into the main
// message so they read as caveats, not part of the alert text itself.
const isTripwireAlert = (alert) =>
  typeof alert?.type === "string" && alert.type.startsWith("line_cross:");

const AlertHistoryPanel = ({
  alerts,
  onAlertClick,
  onAcknowledge,
  onDismiss,
}) => {
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const getSeverityConfig = (severity) => {
    const configs = {
      critical: { color: "red", icon: <RiAlertLine />, text: "緊急" },
      warning: { color: "orange", icon: <RiErrorWarningLine />, text: "警告" },
      info: { color: "blue", icon: <RiInformationLine />, text: "資訊" },
    };
    return configs[severity] || configs.info;
  };

  const formatTimestamp = (timestamp) => {
    const now = moment();
    const alertTime = moment(timestamp);
    const diffSeconds = now.diff(alertTime, "seconds");
    const diffMinutes = now.diff(alertTime, "minutes");
    const diffHours = now.diff(alertTime, "hours");
    const diffDays = now.diff(alertTime, "days");

    if (diffSeconds < 60) {
      return "剛剛";
    } else if (diffMinutes < 60) {
      return `${diffMinutes}分鐘前`;
    } else if (diffHours < 24) {
      const remainingMinutes = diffMinutes % 60;
      if (remainingMinutes === 0) {
        return `${diffHours}小時前`;
      }
      return `${diffHours}小時${remainingMinutes}分鐘前`;
    } else if (diffDays < 7) {
      return `${diffDays}天前`;
    } else {
      // For older alerts, show the actual date
      return alertTime.format("YYYY/MM/DD HH:mm");
    }
  };

  const filteredAlerts = showAcknowledged
    ? alerts
    : alerts.filter((a) => !a.acknowledged);

  const unacknowledgedCount = alerts.filter((a) => !a.acknowledged).length;
  const acknowledgedCount = alerts.filter((a) => a.acknowledged).length;

  return (
    <Card
      title={`警報歷史 (${unacknowledgedCount} 未處理${acknowledgedCount > 0 ? `, ${acknowledgedCount} 已確認` : ""})`}
      size="small"
      bordered
      extra={
        acknowledgedCount > 0 && (
          <Tooltip
            title={showAcknowledged ? "隱藏已確認警報" : "顯示已確認警報"}
          >
            <Space size="small">
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>顯示已確認</span>
              <Switch
                size="small"
                checked={showAcknowledged}
                onChange={setShowAcknowledged}
                checkedChildren={<RiEyeLine />}
                unCheckedChildren={<RiEyeOffLine />}
              />
            </Space>
          </Tooltip>
        )
      }
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
      bodyStyle={{
        padding: 12,
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {filteredAlerts.length === 0 ? (
        <Empty description="暫無警報" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={filteredAlerts}
          renderItem={(alert) => {
            const severityConfig = getSeverityConfig(alert.severity);

            return (
              <List.Item
                style={{
                  padding: "8px 12px",
                  backgroundColor: alert.acknowledged ? "#f5f5f5" : "#fff",
                  borderLeft: `4px solid ${severityConfig.color}`,
                  marginBottom: 8,
                  borderRadius: 4,
                  opacity: alert.acknowledged ? 0.6 : 1,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Space>
                    <Tag
                      color={severityConfig.color}
                      icon={severityConfig.icon}
                    >
                      {severityConfig.text}
                    </Tag>
                    <Tooltip
                      title={moment(alert.timestamp).format(
                        "YYYY/MM/DD HH:mm:ss",
                      )}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: "#8c8c8c",
                          cursor: "help",
                        }}
                      >
                        {formatTimestamp(alert.timestamp)}
                      </span>
                    </Tooltip>
                    {alert.personnelName &&
                      alert.personnelName !== "Unregistered" && (
                        <Tag color="default">{alert.personnelName}</Tag>
                      )}
                    {isTripwireAlert(alert) && (
                      <Tag color="magenta" icon={<RiArrowLeftRightLine />}>
                        越線
                      </Tag>
                    )}
                    <span>{alert.message}</span>
                    {isTripwireAlert(alert) && alert.metadata?.uncertain && (
                      <Tooltip title="移動速度或資料間隔超出合理範圍，此次越線判定為不確定">
                        <Tag color="gold">不確定</Tag>
                      </Tooltip>
                    )}
                    {isTripwireAlert(alert) && alert.metadata?.degraded && (
                      <Tooltip title="部分機具定位器離線，判定基於現有可用定位器">
                        <Tag color="default">部分定位器離線</Tag>
                      </Tooltip>
                    )}
                    {alert.acknowledged && (
                      <Tag color="green" icon={<RiCheckLine />}>
                        已確認
                      </Tag>
                    )}
                  </Space>

                  <Space>
                    <Tooltip title="定位">
                      <Button
                        type="text"
                        size="small"
                        icon={<RiMapPin5Fill />}
                        onClick={() => onAlertClick(alert)}
                      />
                    </Tooltip>
                    {!alert.acknowledged && (
                      <Tooltip title="確認">
                        <Button
                          type="text"
                          size="small"
                          icon={<RiCheckLine />}
                          onClick={() => onAcknowledge(alert.id)}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title="關閉">
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<RiCloseLine />}
                        onClick={() => onDismiss(alert.id)}
                      />
                    </Tooltip>
                  </Space>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </Card>
  );
};

export default AlertHistoryPanel;
